import { useEffect, useMemo, useState } from 'react'
import type { MeasurementPoint, Pallet, PressureFrame, PressureMarker } from '../types/domain'
import { dateTime, number } from './format'
import { convertPressure, formatPressure, pressureUnitLabel, type PressureUnit } from './pressureUnits'

type Plane = 'A' | 'B'

type HeatRow = {
  sensorId: string
  heightMm: number
}

type HeatColumn = {
  time: string
  values: Array<number | null>
}

type HeatSeries = {
  rows: HeatRow[]
  columns: HeatColumn[]
}

type ProfilePoint = HeatRow & {
  rowIndex: number
  value: number
}

type ProfileSelection = {
  time?: string
  columnA?: HeatColumn
  columnB?: HeatColumn
}

export function ForceMeasurementPanel({ pallet, frame, points, unit }: {
  pallet: Pallet
  frame: PressureFrame
  points: MeasurementPoint[]
  unit: PressureUnit
}) {
  const compactDesktop = useDesktopChartLayout()
  const channels = useMemo(() => [...new Set(points.map(point => point.channel))].sort((a, b) => a - b), [points])
  const channelA = channels[0] ?? 0
  const channelB = channels[1] ?? channelA
  const seriesA = useMemo(() => buildHeatSeries(frame.markers, points, channelA), [channelA, frame.markers, points])
  const seriesB = useMemo(() => buildHeatSeries(frame.markers, points, channelB), [channelB, frame.markers, points])
  const timeline = useMemo(() => [...new Set([...seriesA.columns, ...seriesB.columns].map(column => column.time))].sort(), [seriesA.columns, seriesB.columns])
  const [requestedTimes, setRequestedTimes] = useState<[string | undefined, string | undefined]>([undefined, undefined])
  const [activePoint, setActivePoint] = useState<0 | 1>(1)

  useEffect(() => {
    setRequestedTimes([undefined, undefined])
    setActivePoint(1)
  }, [pallet.id])

  const selectedTimes = resolveSelectedTimes(requestedTimes, timeline)
  const selections: [ProfileSelection, ProfileSelection] = selectedTimes.map(time => ({
    time,
    columnA: findColumn(seriesA.columns, time),
    columnB: findColumn(seriesB.columns, time),
  })) as [ProfileSelection, ProfileSelection]
  const scaleValues = [...seriesA.columns, ...seriesB.columns].flatMap(column => numericValues(column.values))
  const scaleMin = scaleValues.length ? Math.min(...scaleValues) : 0
  const scaleMax = scaleValues.length ? Math.max(...scaleValues) : 1

  function selectTime(time: string) {
    setRequestedTimes(current => activePoint === 0 ? [time, current[1]] : [current[0], time])
  }

  return (
    <div className="force-workspace">
      <div className="force-workspace-bar">
        <div className="force-linked-intro">
          <span className="force-workspace-label">Синхронный анализ</span>
          <strong>Две грани · два момента времени</strong>
          <small>Выберите точку 1 или 2, затем укажите её положение на карте. В профиле ниже одновременно показаны четыре дискретные линии.</small>
        </div>
        <div className="force-workspace-side">
          <div className="comparison-points" role="group" aria-label="Выбор точки сравнения">
            {selectedTimes.map((time, index) => <button key={index} type="button" className={activePoint === index ? 'active' : ''} onClick={() => setActivePoint(index as 0 | 1)}>
              <span><i />Точка {index + 1}</span>
              <strong>{time ? exactDateTime(time) : dateTime(frame.timestamp)}</strong>
            </button>)}
          </div>
          <div className="force-export-actions" aria-label="Экспорт измерений">
            <button type="button" onClick={() => downloadCsv(pallet, points)}>Скачать CSV</button>
            <button type="button" onClick={() => window.print()}>Печать / PDF</button>
          </div>
        </div>
      </div>

      <div className="force-linked-charts">
        <CombinedHeatmap
          compact={compactDesktop}
          channelA={channelA}
          channelB={channelB}
          independentChannels={channels.length > 1}
          seriesA={seriesA}
          seriesB={seriesB}
          selectedTimes={selectedTimes}
          activePoint={activePoint}
          scaleMin={scaleMin}
          scaleMax={scaleMax}
          unit={unit}
          onSelect={selectTime}
        />
        <ForceProfile compact={compactDesktop} seriesA={seriesA} seriesB={seriesB} selections={selections} unit={unit} />
      </div>

      <p className="force-method-note">
        Каждая строка карты и каждая точка профиля соответствуют отдельному физическому датчику. Четыре линии сравнивают грани A и B в двух выбранных моментах; пунктир служит только для чтения и не восстанавливает нагрузку между датчиками.
        {channels.length > 1
          ? ` Грань A использует канал ${channelA + 1}, грань B — канал ${channelB + 1}.`
          : ' Источник содержит один канал, поэтому обе карты временно используют общий сигнал.'}
      </p>
    </div>
  )
}

function CombinedHeatmap({ compact, channelA, channelB, independentChannels, seriesA, seriesB, selectedTimes, activePoint, scaleMin, scaleMax, unit, onSelect }: {
  compact: boolean
  channelA: number
  channelB: number
  independentChannels: boolean
  seriesA: HeatSeries
  seriesB: HeatSeries
  selectedTimes: [string | undefined, string | undefined]
  activePoint: 0 | 1
  scaleMin: number
  scaleMax: number
  unit: PressureUnit
  onSelect: (time: string) => void
}) {
  const timeline = [...new Set([...seriesA.columns, ...seriesB.columns].map(column => column.time))].sort()
  const columnsA = new Map(seriesA.columns.map(column => [column.time, column]))
  const columnsB = new Map(seriesB.columns.map(column => [column.time, column]))
  const rows = [
    ...seriesA.rows.map((row, index) => ({ plane: 'A' as Plane, row, index })),
    ...seriesB.rows.map((row, index) => ({ plane: 'B' as Plane, row, index })),
  ]
  const layout = compact
    ? { width: 960, height: 190, plot: { left: 116, top: 8, width: 810, height: 128 }, timeLabelY: 154, axisTitleY: 185 }
    : { width: 680, height: 340, plot: { left: 112, top: 18, width: 520, height: 272 }, timeLabelY: 310, axisTitleY: 337 }
  const plot = layout.plot
  const cellWidth = timeline.length ? plot.width / timeline.length : plot.width
  const cellHeight = plot.height / Math.max(1, rows.length)
  const planeDividerY = plot.top + seriesA.rows.length * cellHeight
  const planeACenterY = plot.top + seriesA.rows.length * cellHeight / 2
  const planeBCenterY = planeDividerY + seriesB.rows.length * cellHeight / 2
  const selectedIndices = selectedTimes.map(time => {
    const index = timeline.findIndex(item => item === time)
    return index >= 0 ? index : Math.max(0, timeline.length - 1)
  })
  const timeFormat = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })

  return (
    <section className="force-chart-card force-heatmap-card" aria-labelledby="heatmap-combined-title">
      <div className="heatmap-heading">
        <div className="mini-heading"><span>Force spectrogram</span><h3 id="heatmap-combined-title">Карта нагрузок двух граней</h3><small>Грань A сверху, грань B снизу · единая временная и цветовая шкала</small></div>
        <div className="plane-channels"><span className="plane-channel">Грань A · верх · {independentChannels ? `канал ${channelA + 1}` : 'общий'}</span><span className="plane-channel secondary">Грань B · низ · {independentChannels ? `канал ${channelB + 1}` : 'общий'}</span></div>
      </div>
      {timeline.length ? <>
        <svg viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label="Тепловая карта нагрузок двух граней с двумя точками сравнения">
          <g className="heatmap-plane-guide plane-a" data-testid="heatmap-plane-label-a">
            <path d={`M 38 ${plot.top + 2} H 30 V ${planeDividerY - 2} H 38`} />
            <text x="17" y={planeACenterY} transform={`rotate(-90 17 ${planeACenterY})`} textAnchor="middle">ГРАНЬ A · ВЕРХ</text>
          </g>
          <g className="heatmap-plane-guide plane-b" data-testid="heatmap-plane-label-b">
            <path d={`M 38 ${planeDividerY + 2} H 30 V ${plot.top + plot.height - 2} H 38`} />
            <text x="17" y={planeBCenterY} transform={`rotate(-90 17 ${planeBCenterY})`} textAnchor="middle">ГРАНЬ B · НИЗ</text>
          </g>
          {timeline.map((time, columnIndex) => rows.map(({ plane, index }, rowIndex) => {
            const value = (plane === 'A' ? columnsA : columnsB).get(time)?.values[index] ?? null
            return <rect
              key={`${time}-${plane}-${index}`}
              x={plot.left + columnIndex * cellWidth}
              y={plot.top + rowIndex * cellHeight}
              width={cellWidth + .6}
              height={cellHeight + .6}
              fill={value === null ? '#ffffff' : heatColor(value, scaleMin, scaleMax)}
              stroke={value === null ? '#d9e2ec' : 'none'}
            />
          }))}
          {rows.map(({ plane, row, index }, rowIndex) => (
            <text key={`${plane}-${row.sensorId}`} x={plot.left - 9} y={plot.top + (rowIndex + .5) * cellHeight + 3} textAnchor="end">
              {plane}{String((plane === 'A' ? seriesA.rows.length : seriesB.rows.length) - index).padStart(2, '0')} · {number(row.heightMm)} мм
            </text>
          ))}
          <line x1={plot.left - 5} x2={plot.left + plot.width} y1={planeDividerY} y2={planeDividerY} className="heatmap-divider" />
          {timeline.map((time, index) => (
            <rect
              key={time}
              data-testid={`heatmap-column-${index}`}
              x={plot.left + index * cellWidth}
              y={plot.top}
              width={cellWidth + .6}
              height={plot.height}
              className="heatmap-hit-area"
              fill="transparent"
              tabIndex={index === selectedIndices[activePoint] ? 0 : -1}
              onPointerEnter={() => onSelect(time)}
              onPointerDown={() => onSelect(time)}
              onFocus={() => onSelect(time)}
            >
              <title>{exactDateTime(time)} · A: {rangeValue(columnsA.get(time)?.values ?? [], unit)} · B: {rangeValue(columnsB.get(time)?.values ?? [], unit)}</title>
            </rect>
          ))}
          {selectedIndices.map((index, point) => timeline[index] && <g key={point} className={`heatmap-cursor-group point-${point + 1}${activePoint === point ? ' active' : ''}`}>
            <line x1={plot.left + (index + .5) * cellWidth} x2={plot.left + (index + .5) * cellWidth}
              y1={plot.top} y2={plot.top + plot.height} className={`heatmap-cursor point-${point + 1}${activePoint === point ? ' active' : ''}`} />
            <rect x={plot.left + (index + .5) * cellWidth - 10} y={plot.top + 3} width="20" height="15" rx="5" className="heatmap-cursor-badge" />
            <text x={plot.left + (index + .5) * cellWidth} y={plot.top + 13.5} textAnchor="middle" className="heatmap-cursor-label">Т{point + 1}</text>
          </g>)}
          <line x1={plot.left} x2={plot.left + plot.width} y1={plot.top + plot.height} y2={plot.top + plot.height} className="heatmap-axis" />
          <text x={plot.left} y={layout.timeLabelY}>{timeFormat.format(new Date(timeline[0]!))}</text>
          <text x={plot.left + plot.width} y={layout.timeLabelY} textAnchor="end">{timeFormat.format(new Date(timeline.at(-1) ?? 0))}</text>
          <text x={plot.left + plot.width / 2} y={layout.axisTitleY} textAnchor="middle" className="axis-title">Время измерения</text>
        </svg>
        <div className="spectrogram-readout comparison" aria-live="polite">
          {selectedTimes.map((time, point) => {
            const columnA = time ? columnsA.get(time) : undefined
            const columnB = time ? columnsB.get(time) : undefined
            return <div key={point} className={`point-${point + 1}`}>
              <span><b>Т{point + 1}</b>{time ? exactDateTime(time) : 'нет данных'}</span>
              <strong><em>A · верх</em>{rangeValue(columnA?.values ?? [], unit)}</strong>
              <strong><em>B · низ</em>{rangeValue(columnB?.values ?? [], unit)}</strong>
            </div>
          })}
        </div>
        <div className="heat-legend"><span>{formatPressure(scaleMin, unit)}</span><i /><span>{formatPressure(scaleMax, unit)}</span></div>
      </> : <div className="force-chart-empty">Нет временных данных для тепловой карты.</div>}
    </section>
  )
}

function ForceProfile({ compact, seriesA, seriesB, selections, unit }: {
  compact: boolean
  seriesA: HeatSeries
  seriesB: HeatSeries
  selections: [ProfileSelection, ProfileSelection]
  unit: PressureUnit
}) {
  const profileSeries = selections.flatMap((selection, moment) => ([
    { key: `${moment}-A`, moment, plane: 'A' as Plane, points: profilePoints(seriesA.rows, selection.columnA, unit) },
    { key: `${moment}-B`, moment, plane: 'B' as Plane, points: profilePoints(seriesB.rows, selection.columnB, unit) },
  ]))
  const allPoints = profileSeries.flatMap(series => series.points)
  const loads = allPoints.map(point => point.value)
  const loadMin = loads.length ? Math.min(...loads) : 0
  const loadMax = loads.length ? Math.max(...loads) : 1
  const loadPadding = Math.max(unit === 'KPA' ? .5 : .005, (loadMax - loadMin) * .06)
  const xMin = Math.max(0, loadMin - loadPadding)
  const xMax = Math.max(xMin + (unit === 'KPA' ? 1 : .01), loadMax + loadPadding)
  const profileRows = seriesA.rows.length ? seriesA.rows : seriesB.rows
  const rowCount = Math.max(1, profileRows.length)
  const layout = compact
    ? { width: 960, height: 195, plot: { left: 112, top: 24, width: 804, height: 112 }, tickY: 154, axisTitleY: 188 }
    : { width: 680, height: 330, plot: { left: 108, top: 34, width: 508, height: 238 }, tickY: 294, axisTitleY: 324 }
  const plot = layout.plot
  const x = (value: number) => plot.left + (value - xMin) / (xMax - xMin) * plot.width
  const y = (rowIndex: number, moment: number) => plot.top + (rowCount === 1 ? .5 : rowIndex / (rowCount - 1)) * plot.height + (moment === 0 ? -3 : 3)
  const digits = unit === 'KPA' ? 1 : 3

  return (
    <section className="force-chart-card force-profile-card" aria-labelledby="profile-title">
      <div className="profile-heading">
        <div className="mini-heading"><span>Force diagram</span><h3 id="profile-title">Профиль двух моментов</h3><small>Грани A и B в точках 1 и 2 · четыре дискретные линии без интерполяции</small></div>
      </div>
      <div className="profile-encoding-guide" aria-label="Обозначения сторон и моментов времени">
        <div className="profile-plane-key">
          <strong>Сторона — цвет и форма точки</strong>
          <span className="plane-a"><i />Грань A · верх карты</span>
          <span className="plane-b"><i />Грань B · низ карты</span>
        </div>
        <div className="profile-moment-key">
          <strong>Момент — тип линии</strong>
          {selections.map((selection, moment) => <span key={moment} className={`moment-${moment + 1}`}><i /><b>Т{moment + 1}</b>{selection.time ? exactDateTime(selection.time) : 'Нет данных'}</span>)}
        </div>
      </div>
      {allPoints.length ? <>
        <svg viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label="Четыре дискретных профиля нагрузки для двух граней и двух моментов времени">
          {profileRows.map((row, rowIndex) => <g key={row.sensorId}>
            {rowIndex % 2 === 0 && <rect x={plot.left} y={Math.max(plot.top, y(rowIndex, 0) - plot.height / Math.max(2, rowCount - 1) / 2)} width={plot.width} height={plot.height / Math.max(1, rowCount - 1)} className="profile-row-band" />}
            <line x1={plot.left} x2={plot.left + plot.width} y1={y(rowIndex, 0) + 3} y2={y(rowIndex, 0) + 3} className="profile-row-line" />
            <text x={plot.left - 10} y={y(rowIndex, 0) + 6} textAnchor="end">У{String(rowIndex + 1).padStart(2, '0')} · {number(row.heightMm)} мм</text>
          </g>)}
          {[0, .25, .5, .75, 1].map(tick => <g key={tick}>
            <line x1={plot.left + plot.width * tick} x2={plot.left + plot.width * tick} y1={plot.top} y2={plot.top + plot.height} stroke="#edf2f7" />
            <text x={plot.left + plot.width * tick} y={layout.tickY} textAnchor="middle">{number(xMin + (xMax - xMin) * tick, digits)}</text>
          </g>)}
          {profileSeries.map(series => {
            const line = series.points.map((point, index) => `${index ? 'L' : 'M'} ${x(point.value)} ${y(point.rowIndex, series.moment)}`).join(' ')
            const className = `profile-series profile-plane-${series.plane.toLowerCase()} profile-moment-${series.moment + 1}`
            return <g key={series.key} className={className}>
              <path d={line} className="profile-series-line" data-testid={`profile-line-${series.plane.toLowerCase()}-${series.moment + 1}`} />
              {series.points.map(point => series.plane === 'A'
                ? <circle key={point.sensorId} data-testid="profile-point-a" cx={x(point.value)} cy={y(point.rowIndex, series.moment)} r="5.5" className="profile-series-point"><title>Точка {series.moment + 1} · грань A · узел {point.rowIndex + 1}: {number(point.value, digits)} {pressureUnitLabel[unit]}</title></circle>
                : <rect key={point.sensorId} data-testid="profile-point-b" x={x(point.value) - 4.5} y={y(point.rowIndex, series.moment) - 4.5} width="9" height="9" rx="1" className="profile-series-point"><title>Точка {series.moment + 1} · грань B · узел {point.rowIndex + 1}: {number(point.value, digits)} {pressureUnitLabel[unit]}</title></rect>)}
            </g>
          })}
          <text x="18" y={plot.top + plot.height / 2} transform={`rotate(-90 18 ${plot.top + plot.height / 2})`} textAnchor="middle" className="axis-title">Измерительный узел · высота</text>
          <text x={plot.left + plot.width / 2} y={layout.axisTitleY} textAnchor="middle" className="axis-title">Нагрузка в измерительном узле, {pressureUnitLabel[unit]}</text>
        </svg>
        <div className="profile-values comparison">
          {profileSeries.map(series => <div key={series.key}><span>Точка {series.moment + 1} · грань {series.plane}</span><strong>{rangeValue(selections[series.moment]?.[series.plane === 'A' ? 'columnA' : 'columnB']?.values ?? [], unit)}</strong></div>)}
        </div>
      </> : <div className="force-chart-empty">Выберите моменты на тепловой карте.</div>}
    </section>
  )
}

function useDesktopChartLayout() {
  const query = '(min-width: 901px)'
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(query)
    const update = () => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return matches
}

function buildHeatSeries(markers: PressureMarker[], points: MeasurementPoint[], channel: number): HeatSeries {
  const rows = [...markers]
    .sort((left, right) => right.zMm - left.zMm)
    .map(marker => ({ sensorId: marker.sensorId, heightMm: marker.zMm }))
  const selectedSensors = new Set(rows.map(row => row.sensorId))
  const grouped = new Map<string, MeasurementPoint[]>()
  points
    .filter(point => point.channel === channel && selectedSensors.has(point.sensorId))
    .forEach(point => grouped.set(point.measuredAt, [...(grouped.get(point.measuredAt) ?? []), point]))
  const source = [...grouped.entries()].sort(([left], [right]) => left.localeCompare(right))
  const step = Math.max(1, Math.ceil(source.length / 96))
  const columns = source
    .filter((_, index) => index % step === 0 || index === source.length - 1)
    .map(([time, entries]) => {
      const bySensor = new Map(entries.map(point => [point.sensorId, point.valueKpa]))
      return { time, values: rows.map(row => bySensor.get(row.sensorId) ?? null) }
    })
  return { rows, columns }
}

function resolveSelectedTimes(requested: [string | undefined, string | undefined], timeline: string[]): [string | undefined, string | undefined] {
  if (!timeline.length) return [undefined, undefined]
  const valid = new Set(timeline)
  const firstDefault = timeline[Math.floor((timeline.length - 1) * .35)] ?? timeline[0]
  const secondDefault = timeline.at(-1)
  return [requested[0] && valid.has(requested[0]) ? requested[0] : firstDefault, requested[1] && valid.has(requested[1]) ? requested[1] : secondDefault]
}

function findColumn(columns: HeatColumn[], time: string | undefined) {
  return columns.find(column => column.time === time) ?? columns.at(-1)
}

function profilePoints(rows: HeatRow[], column: HeatColumn | undefined, unit: PressureUnit): ProfilePoint[] {
  if (!column) return []
  return rows
    .map((row, rowIndex) => ({ ...row, rowIndex, value: column.values[rowIndex] === null ? null : convertPressure(column.values[rowIndex]!, unit) }))
    .filter((point): point is ProfilePoint => point.value !== null && point.value !== undefined)
}

function numericValues(values: Array<number | null>) {
  return values.filter((value): value is number => value !== null)
}

function rangeValue(values: Array<number | null>, unit: PressureUnit) {
  const numeric = numericValues(values)
  if (!numeric.length) return 'Нет данных'
  const digits = unit === 'KPA' ? 1 : 3
  return `${number(convertPressure(Math.min(...numeric), unit), digits)}–${number(convertPressure(Math.max(...numeric), unit), digits)} ${pressureUnitLabel[unit]}`
}

function exactDateTime(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(new Date(value))
}

function heatColor(value: number, min: number, max: number) {
  const t = Math.max(0, Math.min(1, (value - min) / Math.max(.001, max - min)))
  if (t < .5) return mix('#38236e', '#24a47a', t * 2)
  return mix('#24a47a', '#f4dc35', (t - .5) * 2)
}

function mix(from: string, to: string, blend: number) {
  const a = from.match(/\w\w/g)?.map(hex => Number.parseInt(hex, 16)) ?? [0, 0, 0]
  const b = to.match(/\w\w/g)?.map(hex => Number.parseInt(hex, 16)) ?? [0, 0, 0]
  return `rgb(${a.map((channel, index) => Math.round(channel + ((b[index] ?? channel) - channel) * blend)).join(',')})`
}

function downloadCsv(pallet: Pallet, points: MeasurementPoint[]) {
  const header = ['pallet', 'batch', 'measured_at', 'sensor', 'channel', 'pressure_kpa', 'pressure_kgf_cm2', 'quality']
  const rows = points.map(point => [pallet.number, pallet.batchNumber, point.measuredAt, point.sensorSerial, point.channel, point.valueKpa, convertPressure(point.valueKpa, 'KGF_CM2').toFixed(6), point.quality])
  const csv = [header, ...rows].map(row => row.map(csvCell).join(';')).join('\n')
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${pallet.number}-measurements.csv`
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

function csvCell(value: string | number) {
  const text = String(value)
  return /[;"\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}
